/**
 * TagEditor — chip-style tag input with autocomplete from /api/customer-tags.
 *
 * Props:
 *   value         — string[] (lowercased tags)
 *   onChange      — (next: string[]) => void
 *   placeholder   — optional input placeholder
 *   disabled      — disable interactions
 *   max           — optional max tag count
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { X, Tag } from "lucide-react";

const norm = (s) => (s || "").toString().trim().toLowerCase();

export default function TagEditor({ value = [], onChange, placeholder = "Add tag…", disabled, max = 20 }) {
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  // Load tag catalog on focus
  useEffect(() => {
    if (!focused) return;
    api.get("/customer-tags").then((r) => setSuggestions(r.data || [])).catch(() => {});
  }, [focused]);

  const add = (raw) => {
    const t = norm(raw);
    if (!t) return;
    if (value.includes(t)) { setDraft(""); return; }
    if (value.length >= max) return;
    onChange?.([...value, t]);
    setDraft("");
  };
  const remove = (t) => onChange?.(value.filter((x) => x !== t));

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft && value.length) {
      remove(value[value.length - 1]);
    }
  };

  const filtered = suggestions
    .filter((s) => !value.includes(s.tag))
    .filter((s) => !draft || s.tag.includes(norm(draft)))
    .slice(0, 8);

  return (
    <div className="relative w-full" data-testid="tag-editor">
      <div
        className={`flex flex-wrap items-center gap-1.5 min-h-[38px] px-2 py-1.5 rounded border ${
          focused ? "border-indigo-400 ring-1 ring-indigo-200" : "border-slate-200"
        } bg-white`}
        onClick={() => ref.current?.focus()}
      >
        <Tag className="w-3.5 h-3.5 text-slate-400 ml-1" />
        {value.map((t) => (
          <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 gap-1"
                 data-testid={`tag-chip-${t}`}>
            {t}
            {!disabled && (
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(t); }}
                      className="ml-0.5 text-indigo-400 hover:text-indigo-700"
                      data-testid={`tag-remove-${t}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </Badge>
        ))}
        <Input
          ref={ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => setFocused(true)}
          onBlur={() => { setTimeout(() => setFocused(false), 150); add(draft); }}
          placeholder={value.length ? "" : placeholder}
          disabled={disabled || value.length >= max}
          className="flex-1 min-w-[100px] border-0 shadow-none h-7 focus-visible:ring-0 px-1 text-xs"
          data-testid="tag-input"
        />
      </div>
      {focused && filtered.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-44 overflow-y-auto rounded border border-slate-200 bg-white shadow-lg"
             data-testid="tag-suggestions">
          {filtered.map((s) => (
            <button
              key={s.tag}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); add(s.tag); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 flex items-center justify-between"
              data-testid={`tag-suggest-${s.tag}`}
            >
              <span>{s.tag}</span>
              <span className="text-slate-400 text-[10px]">{s.count} use{s.count === 1 ? "" : "s"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
